import React, { useState, useEffect, useCallback } from "react";
import { Save, Loader, View, Sparkles, History } from "lucide-react";
import { Box, IconButton, Tab, ThemeProvider, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { HelpOutline } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import PurchaseGeneral from "./PurchaseGeneral";
import PurchaseOthers from "./PurchaseOthers";
import PurchaseTable from "./PurchaseInvoiceTable";
import OtherCostSection from "./OtherCostModal";
import ConfigCard from "../salesInvoice/ConfigCard";
import SummaryTotalCard from "../salesInvoice/SummaryTotalCard";
import TimelineTab from "../salesInvoice/TimelineTab";
import ModeIndicator from "../salesInvoice/ModeIndicator";
import {
  DOCUMENT_TYPES,
  getDocumentConfig,
  fetchDocumentNumber,
  MODIFICATION_TYPES,
  tooltipContent,
  theme,
  fetchUnitOptionsForItems,
} from "../config/DocumentConfig";
import { purchaseData } from "../salesInvoice/data";
import { getTodayDate } from "../../utils/constants";
import { calculateRowValues } from "../config/PurchaseInvoiceTableConfig";
import Modal from "../common/CommonModal";
import ModificationCommentModal from "../salesInvoice/ModificationCommentModal";
import AiDocumentUploader from "./AiDocumentUploader";
import canUserSeeThis from "../common/CheckCanUserSeeThis";

const PurchaseInvoice = ({
  documentType = DOCUMENT_TYPES.PURCHASE_INVOICE,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();
  const defaultLocation = localStorage.getItem("defaultLocation") || "A";
  const config = getDocumentConfig(documentType);

  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  const CURRENT_USER = localStorage?.getItem("currentUserName") || "admin";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";

  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const todayDate = getTodayDate();
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

  const [aiDocumentUploader, setIsAiDocumentUploader] = useState(false);
  const documentNoFromState = location?.state?.docNo || null;
  const modeFromState = location?.state?.mode || "new";
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(modeFromState);
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString(),
  );
  const [parentTabValue, setParentTabValue] = useState("1");

  const [autoVatIsOn, setAutoVatIsOn] = useState(true);
  const [vatAmountManual, setVatAmountManual] = useState(null);
  const [discountAmount, setDiscountAmount] = useState("");
  const [roundOff, setRoundOff] = useState("");
  const [glSettings, setGlSettings] = useState([]);

  const [items, setItems] = useState(() => {
    const deptLocation =
      localStorage.getItem("departmentLocation") ||
      localStorage.getItem("defaultLocation") ||
      "A";
    return purchaseData.map((item) => ({
      ...item,
      location: item.location || deptLocation,
    }));
  });
  const [initialRowUnitOptions, setInitialRowUnitOptions] = useState({});
  const [showSidebar, setShowSidebar] = useState(false);
  const [currentLocation, setCurrentLocation] = useState("MAIN SHOP");
  const [itemInfo, setItemInfo] = useState({
    qtyInhand: 0,
    costAvg: 0,
    lSalesprice: 0,
    lpCost: 0,
    lCSalesprice: 0,
    pSoQty: "",
    pPoQty: "",
  });

  const [otherCosts, setOtherCosts] = useState([]);
  const [isOtherCostEnabled, setIsOtherCostEnabled] = useState(false);
  const [supplierData, setSupplierData] = useState(null);
  const [transferModalOpen, setIsTransferModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isModificationModalOpen, setIsModificationModalOpen] = useState(false);

  // Document data
  const [documentData, setDocumentData] = useState({
    purchaseType: config.defaultValues.purchaseType || "Local",
    vatStatus: VATPERCENTAGE > 0 ? "VAT Applied" : "0% VAT",
    documentNo: "",
    date: todayDate,
    paymentMode: "credit",
    invNo: "",
    supplier: "",
    unregSuppl: "",
    trn: "",
    dueDays: "",
    dueDate: todayDate,
    cfAgent: "",
    shipRef: "",
    docDate: todayDate,
    jobCode: "",
    remarks: "",
    currency: "AED",
    currencyRate: "1",
    voucherType: "",
    creditAcc: "",
    debitAcc: "",
    invNumber: "",
    vatAcc: "",
    vatAccName: "",
    debitAccName: "",
    vatAccImpName: "",
    creditAccName: "",
  });

  const fetchDocumentDetail = async (documentNo) => {
    try {
      const url = config.apiEndpoints.detail(
        COMPANY_CODE,
        documentNo,
        DEPARTMENT,
      );
      const fullUrl = `${BASE_URL}${url}`;
      const response = await request({ url: fullUrl, method: "GET" });

      if (response && response.length > 0) {
        const rows = response;
        const header = rows[0] || {};

        setDocumentData((prev) => ({
          ...prev,
          documentNo: String(header.inv_no ?? prev.documentNo ?? ""),
          supplier: (header.account || prev.supplier || "").toString().trim(),
          invNo: header.ref || header.inv || prev.invNo || "",
          invNumber: header.inv || prev.invNumber || "",
          trn: header.trn || prev.trn || "",
          currency: header.fc || prev.currency || "AED",
          currencyRate:
            header.rate != null ? String(header.rate) : prev.currencyRate,
          remarks: header.remarks || prev.remarks || "",
          jobCode: header.jobCode || prev.jobCode || "",
          paymentMode: header.cashcred === "credit" ? "credit" : "cash",
          date: header.date ? header.date.slice(0, 10) : prev.date,
          dueDate: header.duedate ? header.duedate.slice(0, 10) : prev.dueDate,
          docDate: header.shipDate
            ? header.shipDate.slice(0, 10)
            : prev.docDate,

          purchaseType:
            header.local === "L" ||
            header.local === "Local" ||
            header.local === "l"
              ? "Local"
              : "Import",
          shipRef: header.shipref,
          debitAcc: header.purchAccount || prev.debitAcc || "",

          creditAcc: header.account || prev.creditAcc || "",

          vatAcc: header.vatacc || prev.vatAcc || "",
          vatAccImp: header.vataccImp || prev.vatAccImp || "",

          voucherType: header.vouchertype || prev.voucherType || "",
          docRef: header.docref || prev.docRef || "",
          CCNO: header.CCNO || prev.CCNO || "",
        }));
        const supplierObj = {
          ACCOUNT: (header.account || "").toString().trim(),
          DESCRIPTION: (header.accdesc || "").toString().trim(),
          TRN: (header.trn || "").toString().trim(),
        };

        setSupplierData(supplierObj);
        setSelectedSupplier(supplierObj);

        setDocumentData((prev) => ({
          ...prev,
          supplier: supplierObj,
          creditAcc:
            documentData.paymentMode === "cash" ? "" : supplierObj.ACCOUNT,
          trn: supplierObj.TRN,
        }));

        setDiscountAmount(
          header.discount ?? header.disc_amt ?? header.discount_amt ?? 0,
        );
        setRoundOff(header.rdoff ?? header.rdoff ?? 0);

        const deptLocation =
          localStorage.getItem("departmentLocation") ||
          localStorage.getItem("defaultLocation") ||
          "A";
        const mappedItems = rows.map((r, idx) => {
          const qty =
            Number(r.tr_qty2 ?? r.qty ?? r.pur_qty ?? r.tr_qty ?? 0) || 0;

          const unitCost = Number(r.funit_pric) || 0;

          const amount =
            Number(r.fline_tota ?? r.amount ?? r.fline_total ?? 0) || 0;

          const othCostPerUnit = Number(r.POTHERCOST ?? r.pothercost ?? 0) || 0;

          return {
            id: idx + 1,
            sino: idx + 1,
            code: (r.code || "").toString().trim(),
            description: r.idesc || r.description || "",
            location: r.locn || r.location || deptLocation,
            unit: r.unit || "",
            qty: qty,
            unitCost: unitCost,
            unitBCCost: r.unit_price,
            amount: amount,
            othCost: othCostPerUnit,
            x: r.x || "",
            cntrl: r.cntrl || "",
            fraction: r.FRACTION ?? r.fraction2 ?? r.fraction ?? 1,
            vatEnabled: r.vatEnabled !== false,
            vatPercent:
              r.vatPercent ??
              (documentData.vatStatus === "VAT Applied" ? VATPERCENTAGE : 0),
            vatAmount: Number(r.vatamt ?? 0),
            total: amount + (Number(r.vatamt ?? 0) || 0),
            mastInfo: r.mastInfo || "",
            pono: r.PO_NO ?? r.pono ?? 0,
            oem: r.oem || "",
            group: r.group || "",
            "Discount%": Number(r.trans_fdiscount ?? 0),
            git: r.git || "",
            salesperson: r.cf,
            bin: r.BATCH || r.bin || "",
            remarks: r.RemarksLine || r.remarks || "",
            dcp: r.DCP || "",
            pono: r.PO_NO || 0,
          };
        });

        setItems(mappedItems);

        // Fetch other costs separately
        await fetchOtherCosts(documentNo);
      }
    } catch (err) {
      console.error(`Failed to fetch ${config.title} detail:`, err);
    }
  };

  // Fetch other costs for the document
  const fetchOtherCosts = async (documentNo) => {
    try {
      const otherCostUrl = `${BASE_URL}/api/DocumentEditDetails/${COMPANY_CODE}/PINVOC/${documentNo}/${DEPARTMENT}`;
      const otherCostResponse = await request({
        url: otherCostUrl,
        method: "GET",
      });

      if (otherCostResponse && otherCostResponse.length > 0) {
        const mappedOtherCosts = otherCostResponse.map((cost, idx) => {
          const vatAmt = Number(cost.v_Amt) || 0;
          const amount = Number(cost.amount - vatAmt) || 0;
          const fcAmount = Number(cost.curamount - vatAmt) || 0;
          const rate = Number(cost.rate) || 1;

          // VAT enabled only if vatAmt > 0
          const vat5Enabled = vatAmt > 0;

          // VAT percent = 5 if enabled, otherwise 0
          const vatPercent = vat5Enabled ? VATPERCENTAGE : 0;

          return {
            id: Date.now() + idx,
            drAcc: cost.account || "",
            drDescription: cost.costc || "",
            ref: cost.ref || "",
            amount: amount.toFixed(2),

            vat5Enabled,
            vatPercent,
            vatAmt: vatAmt.toFixed(2),
            total: (amount + vatAmt).toFixed(2),
            remarks: cost.ttref || "",
            crAcc: cost.pdc_acc || "",
            crDescription: cost.ttref,
            rev: cost.status1 || "N",
            cur: cost.curno || "AED",
            rate,
            fcAmount: fcAmount.toFixed(2),
            baseAmount: amount.toFixed(2),
            lastEdited: null,
          };
        });

        setOtherCosts(mappedOtherCosts);
        setIsOtherCostEnabled(mappedOtherCosts.length > 0);
      }
    } catch (err) {
      console.error("Failed to fetch other costs:", err);
    }
  };
  const fetchGLSettings = useCallback(async () => {
    const url = `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/SCRN_CODE/IR/-/${DEPARTMENT}/-`;
    try {
      const response = await request({ url, method: "GET", timeout: 10000 });
      setGlSettings(response || []);
    } catch (error) {
      console.error("Failed to fetch GL settings", error);
      setGlSettings([]);
    }
  }, [BASE_URL, COMPANY_CODE, DEPARTMENT]);
  useEffect(() => {
    fetchGLSettings();
  }, [fetchGLSettings]);
  const initializeDocument = useCallback(async () => {
    try {
      const { voucherType, documentNo } = await fetchDocumentNumber(
        documentType,
        documentData.paymentMode,
        BASE_URL,
        COMPANY_CODE,
        DEPARTMENT,
      );

      if (documentNo) {
        setDocumentData((prev) => ({
          ...prev,
          documentNo: documentNo,
          voucherType,
        }));
      }
      console.log("Voucher Type:", voucherType, documentNo);
    } catch (err) {
      console.error("Error initializing document:", err);
    }
  }, [
    documentType,
    documentData?.paymentMode,
    BASE_URL,
    COMPANY_CODE,
    DEPARTMENT,
  ]);

  useEffect(() => {
    if (documentNoFromState && request) {
      fetchDocumentDetail(documentNoFromState);
    }
  }, [documentNoFromState, documentType]);

  useEffect(() => {
    if (mode === "new" && documentData.paymentMode) {
      // ensure paymentMode exists
      initializeDocument();
    }
  }, [mode, initializeDocument, documentData.paymentMode]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Auto-save logic: persist new document drafts to localStorage
  useEffect(() => {
    if (
      mode === "new" &&
      items.some((it) => it.code && it.code.trim() !== "")
    ) {
      const draft = {
        items,
      };
      localStorage.setItem(
        `cubix_draft_${documentType}`,
        JSON.stringify(draft),
      );
    }
  }, [items, documentType, mode]);

  const handleRetrieveDraft = useCallback(() => {
    const savedDraft = localStorage.getItem(`cubix_draft_${documentType}`);
    if (savedDraft) {
      const draft = JSON.parse(savedDraft);
      const confirmRestore = window.confirm(
        "Found a previously saved draft for this Purchase Invoice. Would you like to restore it?",
      );
      if (confirmRestore) {
        if (draft.items) setItems(draft.items);
      }
    } else {
      alert("No draft found for this document type.");
    }
  }, [documentType]);

  // Keyboard shortcut listener (Alt + R to restore)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === "r") {
        e.preventDefault();
        handleRetrieveDraft();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleRetrieveDraft]);

  // Navigation Guard: Warn user before leaving with unsaved items
  useEffect(() => {
    const hasUnsavedChanges =
      mode === "new" && items.some((it) => it.code && it.code.trim() !== "");

    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved items in your draft. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [items, mode]);

  // VAT calculation
  const getVatPercent = (documentData) =>
    documentData.vatStatus === "VAT Applied" ? VATPERCENTAGE : 0;

  const calculateTotals = () => {
    const round = (val) => Math.round(Number(val) * 100) / 100;

    const activeItems = items.filter(
      (item) => item.code && String(item.isActive || "").toLowerCase() !== "no",
    );

    const processedItems = activeItems.map((item) =>
      calculateRowValues(item, documentData, {
        items: activeItems,
        otherCosts,
      }),
    );

    // Taxable Amount
    const rowTaxableSum = round(
      processedItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    );

    // VAT Amount
    const rowVatSum = round(
      processedItems.reduce(
        (sum, item) => sum + (Number(item.vatAmount) || 0),
        0,
      ),
    );

    const globalDiscount = Number(discountAmount) || 0;

    const roundOffValue = Number(roundOff) || 0;

    // Taxable Amount after discount
    const taxableAmount = round(rowTaxableSum - globalDiscount);

    const totalVat =
      !autoVatIsOn && vatAmountManual !== null
        ? round(vatAmountManual)
        : globalDiscount > 0
          ? round(taxableAmount * (VATPERCENTAGE / 100))
          : rowVatSum;

    // Gross Amount
    const isImport = documentData.purchaseType === "Import";
    const grossAmount = round(
      isImport ? rowTaxableSum : rowTaxableSum + rowVatSum,
    );

    // Extra Cost
    const totalOtherCosts = (otherCosts || [])
      .filter((oc) => oc.drAcc?.trim() && oc.crAcc?.trim())
      .reduce((sum, oc) => sum + (Number(oc.total || oc.amount) || 0), 0);

    // Net Amount
    const netAmount = round(
      isImport
        ? taxableAmount + roundOffValue
        : taxableAmount + totalVat + roundOffValue,
    );

    const currencyRate = Number(documentData.currencyRate) || 1;

    const bcAmt = round(rowTaxableSum * currencyRate);
    const fcAmt = round(
      (isImport
        ? taxableAmount + roundOffValue
        : taxableAmount + totalVat + roundOffValue) * currencyRate,
    );
    return {
      taxableAmount: taxableAmount.toFixed(2),

      vatAmount: totalVat.toFixed(2),

      grossAmount: grossAmount.toFixed(2),

      discountAmount: globalDiscount.toFixed(2),

      roundOff: roundOffValue.toFixed(2),

      otherCharges: totalOtherCosts.toFixed(2),

      netAmount: netAmount.toFixed(2),

      bcAmt: bcAmt.toFixed(2),
      fcAmt: fcAmt.toFixed(2),
    };
  };
  const handleTransfer = async (docType, selectedRows) => {
    setIsTransferModalOpen(false);
    console.log("Selected rows from Parent component", selectedRows);
    const docNos = selectedRows
      .map((row) => {
        return (
          row.QUOTNO ||
          row.inv_no ||
          row.ORDNO ||
          row.do_no ||
          row.SONO ||
          row.PONO
        );
      })
      .join(",");
    console.log("Doc number transfered", docNos);
    const masterUrl = `${BASE_URL}${config.apiEndpoints.transfer.master(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;
    const detailUrl = `${BASE_URL}${config.apiEndpoints.transfer.detail(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;

    try {
      const masterRes = await fetch(masterUrl);
      const masterData = await masterRes.json();
      console.log("Master Transfer Response:", masterData);

      if (masterData && masterData.length > 0) {
        const totalDiscount = masterData?.reduce(
          (acc, row) => acc + (row.so_disc || 0),
          0,
        );
        const main = masterData[0];

        setDocumentData((prev) => ({
          ...prev,
          supplier: main.cust_acc,
          salesman: main.sale_man?.trim(),
          so_ref: main.so_ref,
          areacode: main.area_code,
          duedate: main.due_date?.slice(0, 10),
          terms: "0",
          date: main.so_date?.slice(0, 10),
          paymentType: "credit",
          currency: main.fc,
          currencyRate: main.so_fcrate || 1,
          remarks: main.comments || "",
          trn: main.CCNO,
          jobCode: main.job_code,
          CCNO: docType === "PORD" ? "PO" : "",
        }));

        setDiscountAmount(totalDiscount);
      }

      const detailRes = await fetch(detailUrl);
      const detailData = await detailRes.json();
      console.log("Detail Transfer Response:", detailData);
      // Item mapping for transfered data
      if (detailData && detailData.length > 0) {
        const items = detailData.map((row, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: row.po_icode || row.code || row.so_icode || row.idesc,
          description: row.idesc,
          location:
            row.po_locn ||
            row.locn ||
            localStorage.getItem("departmentLocation") ||
            localStorage.getItem("defaultLocation") ||
            "A",
          unit: row.unit?.trim(),
          qty: row.po_qty || row.so_qty,
          unitCost: row.po_cost || row.cost_unit || row.so_fccost,
          UnitCost: String(
            Number(row.po_cost || row.cost_unit || row.so_fccost) || 0,
          ),
          discount: row.po_fdisc || row.so_disc || row.so_fdisc,
          fraction: row.fraction,
          cntrl:
            row.souid ||
            row.soGuid ||
            row.soUid ||
            row.doguid ||
            row.uid ||
            row.soguid ||
            "",
          soUid:
            row.souid ||
            row.soGuid ||
            row.soUid ||
            row.doguid ||
            row.uid ||
            row.soguid ||
            "",
          pono: row.ORDNO || row.po_no || 0,
        }));
        setDocumentData((prev) => ({
          ...prev,
          jobCode: detailData[0]?.job_code || "",
        }));
        const initialRowUnitOptions = await fetchUnitOptionsForItems(
          items,
          BASE_URL,
          COMPANY_CODE,
        );
        setItems(items);
        setInitialRowUnitOptions(initialRowUnitOptions);
      }
    } catch (err) {
      console.error("Error fetching transfer data:", err);
    }
  };
  const handleImportExcel = async (rows = []) => {
    if (!rows || rows.length === 0) {
      alert("No rows found in the uploaded file.");
      return;
    }

    try {
      // Normalize keys to lowercase for flexible header names
      const normalized = rows.map((r) => {
        const n = {};
        Object.keys(r || {}).forEach((k) => {
          n[k.toLowerCase().trim()] = r[k];
        });
        return n;
      });

      const mappedItems = normalized.map((r, idx) => {
        const code = r.code || r.prod_code || r.icode || "";
        const description = r.description || r.desc || r.idesc || "";
        const qty = Number(r.qty || r.quantity || 0) || 0;
        const unitCost =
          Number(
            r["unit cost"] ||
              r.unitcost ||
              r.cost ||
              r["unit price"] ||
              r.unitprice ||
              r.price ||
              0,
          ) || 0;
        const unit = (r.unit || "").toString().trim();

        return {
          id: Date.now() + idx,
          sino: idx + 1,
          code: String(code).trim(),
          description: String(description).trim(),
          location: defaultLocation,
          unit,
          qty,
          unitCost, // Using unitCost for Purchase Invoice
          "Discount%": 0,
          discount: 0,
          fraction: "1",
          // vatEnabled: true,
          amount: (qty * unitCost).toFixed(2),
          total: (qty * unitCost).toFixed(2),
        };
      });

      // fetch unit options for these items and set into state
      const initialRowUnitOptions = await fetchUnitOptionsForItems(
        mappedItems,
        BASE_URL,
        COMPANY_CODE,
      );

      setItems(mappedItems);
      setInitialRowUnitOptions(initialRowUnitOptions);

      alert(`Imported ${mappedItems.length} rows from Excel.`);
    } catch (err) {
      console.error("Error importing excel rows", err);
      alert("Import failed. Check console for details.");
    }
  };
  const handleAiDocumentFinalize = async (finalizedData) => {
    console.log("AI Document Finalized Data:", finalizedData);

    // 1. Set payment mode to CREDIT automatically
    // 2. Search for supplier by vendor name
    // 3. Auto-select vendor account
    // 4. Populate all fields

    const vendorName = finalizedData.vendorName || "";
    const vendorAccount = finalizedData.vendorAccount || "";

    const invoiceNumber = finalizedData.invoiceNumber || "";
    const invoiceDate = finalizedData.invoiceDate
      ? finalizedData.invoiceDate.split("T")[0]
      : getTodayDate();

    try {
      // Fetch credit suppliers matching vendor name
      const vendorQuery = encodeURIComponent(vendorAccount);
      const supplierUrl = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT}/Suppliers/-/${vendorQuery}`;

      const supplierResponse = await fetch(supplierUrl);
      const suppliers = await supplierResponse.json();

      console.log("Suppliers found:", suppliers);

      let selectedVendor = null;
      if (suppliers && suppliers.length > 0) {
        // Auto-select first matching supplier
        selectedVendor = suppliers[0];
      }

      // Set document data with all fields
      setDocumentData((prev) => ({
        ...prev,
        paymentMode: "credit", // Auto-set to credit
        supplier: selectedVendor || vendorName,
        invNumber: invoiceNumber,
        date: invoiceDate,
        docDate: invoiceDate,
        dueDate: invoiceDate,
        trn: selectedVendor?.TRN || "",
        creditAcc: selectedVendor?.ACCOUNT || "######",
      }));

      // Set supplier data and selected supplier
      if (selectedVendor) {
        setSupplierData(selectedVendor);
        setSelectedSupplier(selectedVendor);
      }

      // Populate items from AI extraction
      if (finalizedData.items && finalizedData.items.length > 0) {
        const aiItems = finalizedData.items.map((item, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: (item.code || "").toString().trim(),
          description: item.description || "",
          qty: item.quantity || 0,
          unitCost: item.unitPrice || 0,
          unit: "Nos",
          location:
            localStorage.getItem("departmentLocation") ||
            localStorage.getItem("defaultLocation") ||
            "A",
          amount: item.total || 0,
          remarks: "",
          vatEnabled: true,
          vatPercent: 5,
        }));
        setItems(aiItems);
      }

      // Set discount if available
      if (finalizedData.discount && finalizedData.discount > 0) {
        setDiscountAmount(finalizedData.discount.toString());
      }

      // Close the modal
      setIsAiDocumentUploader(false);
    } catch (error) {
      console.error("Error processing AI finalized data:", error);
      alert("Error processing finalized document. Please check the details.");
    }
  };

  const totals = calculateTotals();

  const refreshStockDocument = async (
    companyCode,
    documentNumber,
    department,
  ) => {
    try {
      const url = `${BASE_URL}/api/CRMStockRefreshDoc/${companyCode}/PINV/${documentNumber}/${department}`;
      console.log("Calling refreshStockDocument (PINV) URL:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const refreshResult = await response.json();
      console.log("Stock refresh response (PINV):", refreshResult);

      if (!response.ok) {
        throw new Error(`Stock refresh failed with status: ${response.status}`);
      }

      return refreshResult;
    } catch (error) {
      console.error("Error in refreshStockDocument (PINV):", error);
      return { error: error.message };
    }
  };

  const handleSave = async () => {
    if (!documentData.supplier) {
      alert("Please select a supplier");
      setIsSaving(false);
      return;
    }
    if (!documentData.invNumber) {
      alert("Invoice Number is Required");
      setIsSaving(false);
      return;
    }
    if (
      items.filter((item) => item.code && item.code.trim() !== "").length === 0
    ) {
      alert("Please add at least one item");
      setIsSaving(false);
      return;
    }
    if (otherCosts && otherCosts.length > 0) {
      for (let i = 0; i < otherCosts.length; i++) {
        const cost = otherCosts[i];
        const hasData =
          cost.drAcc?.trim() || cost.crAcc?.trim() || Number(cost.amount) > 0;

        if (hasData) {
          const isValid =
            cost.drAcc?.trim() &&
            cost.crAcc?.trim() &&
            cost.amount &&
            Number(cost.amount) > 0;
          if (!isValid) {
            alert(
              `Other Cost row ${i + 1} is invalid.\nCheck Debit, Credit and Amount.`,
            );
            setIsSaving(false);
            return;
          }
        }
      }
    }
    setIsSaving(true);

    const calculatedItems = items.map((item) =>
      calculateRowValues(item, documentData, { items, otherCosts }),
    );

    const isImport = documentData.purchaseType === "Import";
    const isCash = documentData.paymentMode === "cash";

    const cashAcc = isCash
      ? documentData.creditAcc
      : supplierData?.ACCOUNT || "";
    const finalSupplier =
      supplierData ||
      (typeof documentData.supplier === "object"
        ? documentData.supplier
        : null);

    const supplierAccount = finalSupplier?.ACCOUNT || "";
    const supplierDesc =
      finalSupplier?.DESCRIPTION || finalSupplier?.name || "";

    // --- STOCK CHECK VALIDATION ---
    try {
      const checkPayload = calculatedItems
        .filter((it) => (it?.code || "").toString().trim() !== "")
        .map((it) => ({
          cmpcode: COMPANY_CODE,
          code: String(it.code).trim(),
        }));

      if (checkPayload.length > 0) {
        const response = await fetch(`${BASE_URL}/api/StockCheck`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(checkPayload),
        });

        if (!response.ok) {
          throw new Error("Stock verification API failed.");
        }

        const data = await response.json();

        if (data.notMatchingItems && data.notMatchingItems.length > 0) {
          const missingCodes = data.notMatchingItems
            .map((it) => it.code)
            .join(", ");
          alert(
            `The following items are not found in stock master:\n${missingCodes}\n\nPlease remove or correct these items before saving.`,
          );
          setIsSaving(false);
          return;
        }
      }
    } catch (err) {
      console.error("Stock check failed:", err);
      alert("Stock verification failed. Please check your connection.");
      setIsSaving(false);
      return;
    }

    const documentRows = calculatedItems
      .filter((item) => item.code && item.code.trim() !== "")
      .map((item, idx) => {
        const basePayload = {
          // Company & Operation Info
          cmpcode: COMPANY_CODE,
          modeop: documentNoFromState ? "EDIT" : "SAVE",

          // Document Header
          inv_no: documentData.documentNo || "0000",
          ref: documentData.invNo || "",
          fc: documentData.currency || "AED",
          amount: totals.fcAmt || "0", //Base currency amount (AED)
          fcamount: totals.netAmount || "0", // USD currency amount
          account: supplierAccount,
          accdesc: supplierDesc,
          status: "0",
          remarks: documentData.remarks || "",
          deptno: DEPARTMENT,
          date: documentData.date || todayDate,
          inv: documentData.invNumber || "",
          shipDate: documentData.docDate || todayDate,
          shipref: documentData.shipRef || "",
          duedate: documentData.dueDate || todayDate,
          mawb: "",
          hwab: "",
          lcno: "",
          docref: documentData.docRef || "",
          cfagent: documentData.cfAgent || "",
          purchAccount: documentData.debitAcc,
          purchAccDesc: documentData.debitAccName || "",
          rate: documentData.currencyRate || "1",
          local: documentData.purchaseType === "Local" ? "0" : "1",
          othercost: totals.otherCharges || "0",
          jobCode: documentData.jobCode || "",
          discount: Number(discountAmount) || "0",
          fdiscount: "0",
          CCNO: documentData.CCNO || "",
          SERIALNO: "0",
          vatamt: totals.vatAmount || "0",
          rdoff: roundOff?.toString() || "0",
          costUpd: "NEW",
          docinfo: "",
          mrv_no: "0",
          p1: "0",
          p2: "0",
          vatacc: documentData.vatAcc || "",
          vataccImp: isImport ? documentData.vatAccImp || "" : "",
          method: documentData.paymentMode || "cash",
          cashcred: isCash ? "CASH_PAID" : "",
          cashAcc: cashAcc,
          vatType: documentData.vatStatus || "VAT Applied",

          // Product Info (Line Items)
          Slno: idx + 1,
          code: (item.code || "").toString().trim(),
          description: item.description || "",
          locn: item.location || "",
          unit: item.unit || "",
          qty: item.qty?.toString() || "0",
          "Unit Cost": item.unitCost?.toString() || "0",
          "Unit BCCost":
            item.unitBCCost?.toString() || item.unitCost?.toString() || "0",
          AmountLine: item.amount?.toString() || "0",
          "Oth.Cost": Number(item.othCost || 0).toFixed(2),
          x: item.x || "",
          cntrl: item.cntrl || "",
          frac: item.fraction || "1",
          "vat%": item.VATPERCENTAGE?.toString() || "5",
          VatamtLine: item.vatAmount?.toString() || "0",
          Mastinfo: item.mastInfo || "",
          pono: item.pono || "0",
          total: item.total?.toString() || "0",
          oem: item.oem || "",
          group: item.group || "",
          "disc%": Number(item["Discount%"]) || "0",
          git: item.git || "0",
          Salesperson: item.salesperson || "",
          bin: item.bin || "",
          RemarksLine: item.remarks || "",
          DCP: item.dcp || "0",
        };

        return basePayload;
      });

    console.log(`${config.title} Rows:`, documentRows);
    console.log("Account Mapping:", {
      paymentMode: documentData.paymentMode,
      cashAcc: cashAcc,
      vatacc: documentData.vatAcc,
      vataccImp: documentData.vatAccImp,
      creditAcc: documentData.creditAcc,
    });
    // return;

    try {
      // First API: Save main document
      const response = await fetch(`${BASE_URL}${config.apiEndpoints.save}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(documentRows),
      });

      const result = await response.json();
      console.log("Response from API:", result);
      if (response?.ok && result) {
        const invoiceNumber =
          mode === "edit" || documentNoFromState
            ? documentData.documentNo
            : result?.invoiceNo ||
              result?.srno ||
              result?.sr_no ||
              documentData.documentNo ||
              "";

        if (invoiceNumber) {
          console.log(`Triggering stock refresh for PINV: ${invoiceNumber}`);
          refreshStockDocument(COMPANY_CODE, invoiceNumber, DEPARTMENT).catch(
            (err) => {
              console.error("Stock refresh failed:", err);
            },
          );
        }

        // Second API: Save other costs if it has valid data
        if (otherCosts && otherCosts.length > 0) {
          const validOtherCosts = otherCosts.filter(
            (cost) =>
              cost.drAcc?.trim() &&
              cost.crAcc?.trim() &&
              Number(cost.amount) > 0,
          );

          if (validOtherCosts.length > 0) {
            const vatAccount =
              glSettings?.find((g) => g.Scrn_code === "IR-VT-D")?.ACCOUNT || "";
            const vatRcmAccount =
              glSettings?.find((g) => g.Scrn_code === "IR-VT-C")?.ACCOUNT || "";

            const otherCostPayload = validOtherCosts.flatMap((cost, index) => {
              const entries = [];
              const hasVat = Number(cost.vatAmt) > 0;
              const isRcm = cost.rcm === "Y" || cost.rev === "Y";

              // 1. Base Debit Entry
              entries.push({
                cmpcode: COMPANY_CODE,
                operation: "ENTRY",
                account: cost.drAcc || "",
                jvtype: "IR",
                jvnum: invoiceNumber || "",
                jvdate: documentData.date || todayDate,
                ref: cost.ref || "",
                entry_ref: documentData.purchaseType || "Local",
                type: "D",
                amount: cost.amount || "0.00",
                curno: cost.cur || "AED",
                curamount: cost.amount || cost.amount || "0.000",
                user_id: CURRENT_USER,
                type_no: invoiceNumber || "",
                accountype: index + 1,
                job_code: documentData.jobCode || "",
                sman_code: "",
                depn_p: "0",
                costc: cost.drDescription || "",
                chqno: "",
                chqdate: "",
                rate: cost.rate?.toString() || "1.000000",
                pdcstatus: "",
                pdc_acc: cost.crAcc || "",
                status1: isRcm ? "Y" : "N",
                status: "",
                appl_amt1: "0.000",
                appl_amt: "0.000",
                bank: "",
                due_date: documentData.dueDate || todayDate,
                doc_date: documentData.docDate || todayDate,
                month: "",
                lpono: "",
                deptno: DEPARTMENT,
                chqprn: "false",
                inv_amt: cost.amount || "0.00",
                inv_fcamt: "0",
                inv_fc: "0",
                ccno: "",
                ttref: cost.crDescription || "",
                appl_cramt: "0.000",
                appl_crop: "5",
                appl_dramt: "0",
                pgroup: "",
                upd: "0",
                v_amt: cost.vatAmt || "0.00",
                COSTCODE: "OC",
              });

              // 2. Base Credit Entry
              entries.push({
                cmpcode: COMPANY_CODE,
                operation: "ENTRY",
                account: cost.crAcc || "",
                jvtype: "IR",
                jvnum: invoiceNumber || "",
                jvdate: documentData.date || todayDate,
                ref: cost.ref || "",
                entry_ref: documentData.purchaseType || "Local",
                type: "C",
                amount: isRcm ? cost.amount : cost.total || "0.00",
                curno: cost.cur || "AED",
                curamount: cost.amount / cost.rate || cost.amount || "0.000",
                user_id: CURRENT_USER,
                type_no: invoiceNumber || "",
                accountype: index + 2,
                job_code: documentData.jobCode || "",
                sman_code: "",
                depn_p: "0",
                costc: cost.crDescription || "",
                chqno: "",
                chqdate: "",
                rate: cost.rate?.toString() || "1.000000",
                pdcstatus: "",
                pdc_acc: cost.drAcc || "",
                status1: "",
                status: "",
                appl_amt1: "0.000",
                appl_amt: "0.000",
                bank: "",
                due_date: documentData.dueDate || todayDate,
                doc_date: documentData.docDate || todayDate,
                month: "",
                lpono: "",
                deptno: DEPARTMENT,
                chqprn: "false",
                inv_amt: cost.amount || "0.00",
                inv_fcamt: "0",
                inv_fc: "0",
                ccno: "",
                ttref: cost.drDescription || "",
                appl_cramt: "0.000",
                appl_crop: "5",
                appl_dramt: "0",
                pgroup: "",
                upd: "0",
                v_amt: "0",
                COSTCODE: "OC",
              });

              // 3. VAT Entries (Only included if VAT amount is greater than 0)
              if (hasVat) {
                // VAT Debit Entry
                entries.push({
                  cmpcode: COMPANY_CODE,
                  operation: "ENTRY",
                  account: vatAccount,
                  jvtype: "IR",
                  jvnum: invoiceNumber || "",
                  jvdate: documentData.date || todayDate,
                  ref: cost.ref || "",
                  entry_ref: documentData.purchaseType || "Local",
                  type: "D",
                  amount: cost.vatAmt || "0.00",
                  curno: cost.cur || "AED",
                  curamount: cost.vatAmt || "0.000",
                  user_id: CURRENT_USER,
                  type_no: invoiceNumber || "",
                  accountype: index + 3,
                  job_code: documentData.jobCode || "",
                  sman_code: "",
                  depn_p: "0",
                  costc: cost.crDescription || "",
                  chqno: "",
                  chqdate: "",
                  rate: cost.rate?.toString() || "1.000000",
                  pdcstatus: "",
                  pdc_acc: cost.drAcc || "",
                  status1: "",
                  status: "V",
                  appl_amt1: "0.000",
                  appl_amt: "0.000",
                  bank: "",
                  due_date: documentData.dueDate || todayDate,
                  doc_date: documentData.docDate || todayDate,
                  month: "",
                  lpono: "",
                  deptno: DEPARTMENT,
                  chqprn: "false",
                  inv_amt: cost.total - cost.vatAmt,
                  inv_fcamt: "0",
                  inv_fc: "0",
                  ccno: "",
                  ttref: cost.drDescription || "",
                  appl_cramt: "0.000",
                  appl_crop: "5",
                  appl_dramt: "0",
                  pgroup: "",
                  upd: "0",
                  v_amt: cost.vatAmt,
                  COSTCODE: isRcm ? "OC_REV" : "OC",
                });
                // VAT Credit Entry only when isRCM
                // 4. RCM Offset Entry (Only included if RCM/Reverse Charge is selected)
                if (isRcm) {
                  entries.push({
                    cmpcode: COMPANY_CODE,
                    operation: "ENTRY",
                    account: vatRcmAccount,
                    jvtype: "IR",
                    jvnum: invoiceNumber || "",
                    jvdate: documentData.date || todayDate,
                    ref: cost.ref || "",
                    entry_ref: "RCM",
                    type: "C",
                    amount: cost.vatAmt || "0.00",
                    curno: cost.cur || "AED",
                    curamount: cost.vatAmt || "0.000",
                    user_id: CURRENT_USER,
                    type_no: invoiceNumber || "",
                    accountype: "1",
                    job_code: documentData.jobCode || "",
                    sman_code: "",
                    depn_p: "0",
                    costc: cost.crDescription || "",
                    chqno: "",
                    chqdate: "",
                    rate: cost.rate?.toString() || "1.000000",
                    pdcstatus: "",
                    pdc_acc: cost.drAcc || "",
                    status1: "",
                    status: "V",
                    appl_amt1: "0.000",
                    appl_amt: "0.000",
                    bank: "",
                    due_date: documentData.dueDate || todayDate,
                    doc_date: documentData.docDate || todayDate,
                    month: "",
                    lpono: "",
                    deptno: DEPARTMENT,
                    chqprn: "false",
                    inv_amt: cost.total - cost.vatAmt,
                    inv_fcamt: "0",
                    inv_fc: "0",
                    ccno: "",
                    ttref: cost.drDescription || "",
                    appl_cramt: "0.000",
                    appl_crop: "5",
                    appl_dramt: "0",
                    pgroup: "",
                    upd: "0",
                    v_amt: cost.vatAmt,
                    COSTCODE: "OC_REV",
                  });
                }
              }

              return entries;
            });

            console.log("Other Cost Payload:", otherCostPayload);

            try {
              const otherCostResponse = await fetch(
                `${BASE_URL}/api/CRMActransReg`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(otherCostPayload),
                },
              );

              const otherCostResult = await otherCostResponse.json();
              console.log("Other Cost API Response:", otherCostResult);

              if (!otherCostResponse?.ok || !otherCostResult) {
                console.error("Failed to save other costs:", otherCostResult);
                alert(
                  `${config.title} failed to save other costs. Please check and try again.`,
                );
              }
            } catch (otherCostError) {
              console.error("Error saving other costs:", otherCostError);
              alert(`${config.title} error occurred while saving other costs.`);
            }
          }
        }

        alert(`${config.title} no: ${invoiceNumber} saved successfully.`);
        navigate("/purchaseInvoiceList");
        clearAllFields();
        initializeDocument();
      } else {
        alert(`Failed to save ${config.title}.`);
      }
    } catch (error) {
      console.error(`Error saving ${config.title}:`, error);
      alert(`Error saving ${config.title}.`);
    } finally {
      // Clear loading state
      setIsSaving(false);
    }
  };
  const clearAllFields = () => {
    setDocumentData({
      purchaseType: config.defaultValues.purchaseType,
      vatStatus: "VAT Applied",
      documentNo: "",
      date: todayDate,
      paymentMethod: config.defaultValues.paymentMethod,
      invNo: "",
      supplier: "",
      unregSuppl: "",
      trn: "",
      dueDays: "",
      dueDate: todayDate,
      cfAgent: "",
      shipRef: "",
      docDate: todayDate,
      jobCode: "",
      remarks: "",
      currency: "AED",
      currencyRate: "1",
      voucherType: "",
    });

    setItems(purchaseData);
    setOtherCosts([]);
    setIsOtherCostEnabled(false);
    setDiscountAmount("");
    setRoundOff("");
    setMode("new");
  };

  const handlePrint = () => {
    navigate("/purchaseInvoiceList");
  };

  const handleParentTabChange = (event, newValue) => {
    setParentTabValue(newValue);
  };

  return (
    <div className="w-full relative">
      <div className="flex items-center justify-between lg:px-10 px-2 mb-2 gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">{config.title}</h1>
        </div>

        <Tooltip title={tooltipContent} sx={{ margin: 0, padding: 0 }}>
          <IconButton>
            <HelpOutline
              className="text-blue-600 bg-white rounded-full"
              fontSize="medium"
            />
          </IconButton>
        </Tooltip>
      </div>
      <ModeIndicator mode={mode} documentType={documentType} />
      <div className="w-full rounded-lg">
        <div className="lg:px-10">
          <div className="bg-gray-50 border lg:p-3 rounded-lg shadow pb-6">
            <ThemeProvider theme={theme}>
              <TabContext value={parentTabValue}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TabList onChange={handleParentTabChange}>
                    <Tab label="General" value="1" sx={{ padding: 0 }} />
                    <Tab label="Others" value="2" sx={{ padding: 0 }} />
                    <Tab label="Timeline" value="3" sx={{ padding: 0 }} />
                  </TabList>
                  <div className="lg:flex hidden items-center gap-3">
                    {API_PATHS[0]?.AI_FEATURE === "true" &&
                      canUserSeeThis("ZD") && (
                        <button
                          onClick={() => setIsAiDocumentUploader(true)}
                          className="relative group flex items-center gap-2 px-5 py-2.5 bg-[#1e1b4b] hover:bg-[#2e2a70] border border-indigo-400/30 text-white rounded-lg transition-all duration-300 shadow-[0_4px_12px_rgba(49,46,129,0.3)] hover:shadow-[0_6px_20px_rgba(99,102,241,0.4)] active:scale-95 overflow-hidden"
                        >
                          {/* The "Neural Network" Glow effect */}
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-500/20 via-transparent to-transparent transition-opacity duration-500" />

                          {/* Animated Shimmer Streak */}
                          <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                          {/* Sparkle Icon */}
                          <div className="relative">
                            <Sparkles className="w-4 h-4 text-indigo-300 group-hover:text-white group-hover:rotate-12 transition-all" />
                            {/* Intelligent Pulse Dot */}
                            <span className="absolute -top-1 -right-1 flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-indigo-300"></span>
                            </span>
                          </div>

                          {/* Button Text */}
                          <span className="relative text-sm font-bold tracking-tight text-indigo-50">
                            AI Smart Upload
                          </span>

                          {/* Subtle "Active" Indicator at the bottom */}
                          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 group-hover:w-1/2 h-[1px] bg-gradient-to-r from-transparent via-indigo-300 to-transparent transition-all duration-500" />
                        </button>
                      )}
                    <button
                      onClick={handleRetrieveDraft}
                      className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white text-sm font-medium rounded-lg shadow hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400 transition active:scale-95"
                      title="Restore last unsaved draft (Alt+R)"
                    >
                      <History className="w-4 h-4" />
                      Restore (Alt + R)
                    </button>
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-2 px-5 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg shadow hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <View className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        // if (mode === "edit") {
                        //   setIsModificationModalOpen(true);
                        // } else {
                        handleSave();
                        // }
                      }}
                      disabled={mode === "view" || isSaving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {mode === "edit" ? "Update" : "Save"}
                        </>
                      )}
                    </button>
                  </div>
                </Box>
                <TabPanel value="1" sx={{ p: 0, paddingX: 0 }}>
                  <PurchaseGeneral
                    purchaseInvoiceData={documentData}
                    setPurchaseInvoiceData={setDocumentData}
                    COMPANY_CODE={COMPANY_CODE}
                    DEPARTMENT_NO={DEPARTMENT}
                    BASE_URL={BASE_URL}
                    setSupplierData={setSupplierData}
                    selectedSupplier={selectedSupplier}
                    setSelectedSupplier={setSelectedSupplier}
                    supplierData={supplierData}
                    documentType="PURCHASEINVOICE"
                    mode={mode}
                    glSettings={glSettings}
                    setGlSettings={setGlSettings}
                  />
                </TabPanel>
                <TabPanel value="2" sx={{ p: 0, paddingX: 0 }}>
                  <PurchaseOthers
                    purchaseOthersData={documentData}
                    setPurchaseOthersData={setDocumentData}
                    isOtherCostEnabled={isOtherCostEnabled}
                    setIsOtherCostEnabled={setIsOtherCostEnabled}
                  />
                </TabPanel>
                <TabPanel value="3" sx={{ p: 1 }}>
                  <TimelineTab
                    BASE_URL={BASE_URL}
                    DEPARTMENT={DEPARTMENT}
                    modificationType={MODIFICATION_TYPES[documentType] || null}
                    invoiceId={documentNoFromState}
                    COMPANY_CODE={COMPANY_CODE}
                  />
                </TabPanel>
              </TabContext>
            </ThemeProvider>
          </div>
        </div>

        <main className="w-full mt-4 relative">
          <PurchaseTable
            items={items}
            onItemsChange={setItems}
            setShowSidebar={setShowSidebar}
            u
            setItemInfo={setItemInfo}
            setCurrentLocation={setCurrentLocation}
            extraFields={[]}
            purchaseData={documentData}
            initialRowUnitOptions={initialRowUnitOptions}
            setInitialRowUnitOptions={setInitialRowUnitOptions}
            isOtherCostEnabled={isOtherCostEnabled}
            setIsOtherCostEnabled={setIsOtherCostEnabled}
            otherCosts={otherCosts}
            setOtherCosts={setOtherCosts}
            calculateRowValues={calculateRowValues}
            invoiceData={documentData}
            getVatPercent={getVatPercent}
            BASE_URL={BASE_URL}
            COMPANY_CODE={COMPANY_CODE}
            DEPARTMENT_NO={DEPARTMENT}
            documentType={documentType}
          />

          {isOtherCostEnabled && (
            <div className="lg:w-[calc(100vw-7vw)] w-dvw overflow-x-auto lg:px-10 mt-2">
              <OtherCostSection
                otherCosts={otherCosts}
                setOtherCosts={setOtherCosts}
                purchaseItems={items}
                BASE_URL={BASE_URL}
                COMPANY_CODE={COMPANY_CODE}
                invoiceNoFromState={documentNoFromState}
              />
            </div>
          )}
        </main>

        <div className="w-full lg:px-10 p-1 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <ConfigCard
              invoiceData={documentData}
              setAutoVatIsOn={setAutoVatIsOn}
              setInvoiceData={setDocumentData}
              autoVatIsOn={autoVatIsOn}
              documentType={documentType}
              config={config}
              setIsModalOpen={setIsTransferModalOpen}
              isModalOpen={transferModalOpen}
              fetchDocumentDetail={fetchDocumentDetail}
              invoiceNoFromState={documentNoFromState}
              accountData={supplierData}
              onTransfer={handleTransfer}
              onImportExcel={handleImportExcel}
            />

            <SummaryTotalCard
              totals={totals}
              setInvoiceData={setDocumentData}
              invoiceData={documentData}
              handlePrint={handlePrint}
              handleSave={handleSave}
              roundOff={roundOff}
              setRoundOff={setRoundOff}
              discountAmount={discountAmount}
              setDiscountAmount={setDiscountAmount}
              mode={mode}
              autoVatIsOn={autoVatIsOn}
              setVatAmountManual={setVatAmountManual}
              config={config}
              setIsModificationModalOpen={setIsModificationModalOpen}
            />
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModificationModalOpen}
        onClose={() => setIsModificationModalOpen(false)}
        size="md"
        header={true}
        title="Update Confirmation"
        showFullScreen={false}
      >
        <ModificationCommentModal
          isOpen={isModificationModalOpen}
          onClose={() => setIsModificationModalOpen(false)}
          handleSave={handleSave}
          documentData={documentData}
          documentType={documentType}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          CURRENT_USER={CURRENT_USER}
          BASE_URL={BASE_URL}
          setIsModificationModalOpen={setIsModificationModalOpen}
        />
      </Modal>
      <Modal
        isOpen={aiDocumentUploader}
        onClose={() => setIsAiDocumentUploader(false)}
        size="xxl"
        header={true}
        title="AI Document Uploader"
        showFullScreen={true}
      >
        <AiDocumentUploader
          onClose={() => setIsAiDocumentUploader(false)}
          onFinalize={handleAiDocumentFinalize}
        />
      </Modal>{" "}
    </div>
  );
};

export default PurchaseInvoice;
